import { useRef, useState, useCallback, useEffect } from 'react';
import { Camera, Upload, X, RefreshCw, Check } from 'lucide-react';

interface SelfieCaptureProps {
  onCapture: (base64: string | null) => void;
}

/** Crop video frame to 400×400 JPEG using Canvas API. Raw frames never leave the browser. */
function cropToSquare(video: HTMLVideoElement): string {
  const canvas = document.createElement('canvas');
  const size = Math.min(video.videoWidth, video.videoHeight);
  canvas.width = 400;
  canvas.height = 400;
  const ctx = canvas.getContext('2d')!;
  const offsetX = (video.videoWidth - size) / 2;
  const offsetY = (video.videoHeight - size) / 2;
  ctx.drawImage(video, offsetX, offsetY, size, size, 0, 0, 400, 400);
  return canvas.toDataURL('image/jpeg', 0.85);
}

/** Crop a File image to 400×400 JPEG. */
async function cropFileToSquare(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const size = Math.min(img.naturalWidth, img.naturalHeight);
      canvas.width = 400;
      canvas.height = 400;
      const ctx = canvas.getContext('2d')!;
      const offsetX = (img.naturalWidth - size) / 2;
      const offsetY = (img.naturalHeight - size) / 2;
      ctx.drawImage(img, offsetX, offsetY, size, size, 0, 0, 400, 400);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/jpeg', 0.85));
    };
    img.onerror = () => reject(new Error('Image load failed'));
    img.src = url;
  });
}

export default function SelfieCapture({ onCapture }: SelfieCaptureProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [preview, setPreview] = useState<string | null>(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [skipped, setSkipped] = useState(false);

  const startCamera = useCallback(async () => {
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      streamRef.current = stream;
      // videoRef is always mounted (just hidden via CSS), so srcObject can be
      // set immediately — no useEffect timing dance needed.
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch(() => {});
      }
      setCameraActive(true);
    } catch {
      setCameraError('Camera access denied. Please use the file upload instead.');
    }
  }, []);

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setCameraActive(false);
  }, []);

  const captureFrame = useCallback(() => {
    if (!videoRef.current) return;
    const b64 = cropToSquare(videoRef.current);
    stopCamera();
    setPreview(b64);
    onCapture(b64);
  }, [onCapture, stopCamera]);

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      try {
        const b64 = await cropFileToSquare(file);
        setPreview(b64);
        onCapture(b64);
      } catch {
        setCameraError('Could not process image. Please try another file.');
      }
    },
    [onCapture],
  );

  const handleRetake = useCallback(() => {
    setPreview(null);
    setSkipped(false);
    onCapture(null);
  }, [onCapture]);

  const handleSkip = useCallback(() => {
    stopCamera();
    setPreview(null);
    setSkipped(true);
    onCapture(null);
  }, [onCapture, stopCamera]);

  // Stop camera stream when component unmounts (prevents camera light staying on)
  useEffect(() => () => { streamRef.current?.getTracks().forEach((t) => t.stop()); }, []);

  if (skipped) {
    return (
      <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
        <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-gray-400 text-xs">
          <Camera className="h-5 w-5" />
        </div>
        <span className="text-sm text-gray-500 dark:text-gray-400">Selfie skipped — a placeholder will be used on your access card.</span>
        <button
          type="button"
          onClick={handleRetake}
          className="ml-auto text-xs text-blue-500 hover:underline"
        >
          Add selfie
        </button>
      </div>
    );
  }

  if (preview) {
    return (
      <div className="flex flex-col items-center gap-3">
        <img
          src={preview}
          alt="Selfie preview"
          className="w-24 h-24 rounded-full object-cover border-4 border-green-500"
        />
        <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
          <Check className="h-4 w-4" />
          Selfie captured
        </div>
        <button
          type="button"
          onClick={handleRetake}
          className="flex items-center gap-1 text-xs text-blue-500 hover:underline"
        >
          <RefreshCw className="h-3 w-3" />
          Retake
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Video is always present in the DOM so videoRef.current is available
          the moment startCamera runs — it is just visually hidden until active. */}
      <div className={`flex flex-col items-center gap-3${cameraActive ? '' : ' hidden'}`}>
        <video
          ref={videoRef}
          className="w-48 h-48 rounded-full object-cover border-2 border-blue-400"
          autoPlay
          muted
          playsInline
        />
        <div className="flex gap-2">
          <button
            type="button"
            onClick={captureFrame}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium flex items-center gap-2"
          >
            <Camera className="h-4 w-4" />
            Take Photo
          </button>
          <button
            type="button"
            onClick={stopCamera}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-1"
          >
            <X className="h-4 w-4" />
            Cancel
          </button>
        </div>
      </div>

      {!cameraActive && (
        <div className="flex flex-wrap gap-2 justify-center">
          <button
            type="button"
            onClick={startCamera}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium flex items-center gap-2"
          >
            <Camera className="h-4 w-4" />
            Use Camera
          </button>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
          >
            <Upload className="h-4 w-4" />
            Upload Photo
          </button>
          <button
            type="button"
            onClick={handleSkip}
            className="px-4 py-2 text-gray-500 dark:text-gray-400 rounded-lg text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            Skip
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileChange}
          />
        </div>
      )}

      {cameraError && (
        <p className="text-xs text-red-500 text-center">{cameraError}</p>
      )}
    </div>
  );
}
